type NavigationContext = {
  streamId?: string;
  projectId?: string;
  repositoryId?: string;
  memberUserId?: string;
};

/**
 * Derive Vanti navigation scope from the current React Router location.
 */
export function navigationContextFromRoute(
  pathname: string,
  params: Record<string, unknown> = {}
): NavigationContext | null {
  const p = pathname || '';
  const out: NavigationContext = {};

  const streamId = params.streamId ? String(params.streamId).trim() : '';
  const projectId = params.projectId ? String(params.projectId).trim() : '';
  if (streamId) out.streamId = streamId;
  if (projectId) out.projectId = projectId;

  if (!out.projectId) {
    const projectMatch = p.match(/^\/streams\/[^/]+\/projects\/([^/]+)/);
    if (projectMatch) out.projectId = projectMatch[1];
  }
  if (!out.streamId) {
    const streamMatch = p.match(/^\/streams\/([^/]+)/);
    if (streamMatch && streamMatch[1] !== 'projects') {
      out.streamId = streamMatch[1];
    }
  }

  const repoMatch = p.match(/^\/repositories\/([^/]+)/);
  if (repoMatch) out.repositoryId = repoMatch[1];

  const memberId = params.memberId ? String(params.memberId).trim() : '';
  if (memberId) {
    out.memberUserId = memberId;
  } else {
    const memberMatch = p.match(/^\/members\/([^/]+)/);
    if (memberMatch) out.memberUserId = memberMatch[1];
  }

  if (!out.streamId && !out.projectId && !out.repositoryId && !out.memberUserId) {
    return null;
  }
  return out;
}

/**
 * Human-readable chip label from API scope meta or local navigation context.
 * @param {object|null|undefined} scope
 */
export function formatNavigationScopeLabel(scope: Record<string, unknown> | null | undefined) {
  if (!scope || typeof scope !== 'object') return '';
  const parts: string[] = [];
  if (scope.projectName) parts.push(`Project ${scope.projectName}`);
  else if (scope.streamName) parts.push(`Stream ${scope.streamName}`);
  if (scope.repositoryFullName) parts.push(`Repo ${scope.repositoryFullName}`);
  if (scope.memberDisplayName) parts.push(`Member ${scope.memberDisplayName}`);
  if (!parts.length) {
    if (scope.projectId) parts.push('Project');
    else if (scope.streamId) parts.push('Stream');
    else if (scope.repositoryId) parts.push('Repository');
    else if (scope.memberUserId) parts.push('Member');
  }
  return parts.join(' · ');
}

/**
 * Build navigationContext payload from a scope-pick candidate.
 * @param {{ kind: string, id: string, streamId?: string, projectId?: string }} candidate
 */
export function navigationContextFromPick(candidate: {
  kind: string;
  id: string;
  streamId?: string;
  projectId?: string;
}): (NavigationContext & { source: string }) | null {
  if (!candidate?.kind || !candidate?.id) return null;
  const ctx: NavigationContext & { source: string } = { source: 'explicit' };
  if (candidate.kind === 'project') {
    ctx.projectId = candidate.id;
    if (candidate.streamId) ctx.streamId = candidate.streamId;
  } else if (candidate.kind === 'repository') {
    ctx.repositoryId = candidate.id;
    if (candidate.projectId) ctx.projectId = candidate.projectId;
  } else if (candidate.kind === 'stream') {
    ctx.streamId = candidate.id;
  } else if (candidate.kind === 'member') {
    ctx.memberUserId = candidate.id;
  }
  return ctx;
}
